import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import objectHash from 'object-hash';

import Leg from 'components/shared/Leg';
import PsuedoStationDepartureTime from 'components/shared/PsuedoStationDepartureTime';
import Table from 'components/ui/Table';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import AfterSalesSummaryBox from 'pages/BookingDetails/AfterSalesSummaryBox';
import { legShape, productsShape } from 'constants/propTypes';
import { passengerTypes } from 'types/passengers';

import { colours } from 'themes/default';

const mapToPassengerData = (bookingItem, isSelectable) => {
  const { passenger, currency } = bookingItem;

  return {
    key: objectHash(passenger),
    firstName: passenger.firstName,
    lastName: passenger.lastName,
    passengerType: <FormattedMessage id={`passengerTypes.${passengerTypes[passenger.type]}`} />,
    ticketPrice: <FormattedCurrency currency={currency} amount={bookingItem.price} />,
    ticketNumber: bookingItem.ticketNumber,
    status: bookingItem && <FormattedMessage id={`bookingStatus.${bookingItem.status}`} />,
    ...(isSelectable && { select: bookingItem })
  };
};

const ColumnKeys = [
  {
    key: 'firstName'
  },
  {
    key: 'lastName'
  },
  {
    key: 'passengerType'
  },
  {
    key: 'ticketPrice'
  },
  {
    key: 'ticketNumber'
  },
  {
    key: 'status'
  }
];

const FareAddOnLeg = ({
  direction,
  leg,
  includeFareConditions,
  activeBookingItems,
  formatHeader,
  bookingItems,
  isGroupChecked,
  isSelectable,
  toggleGroup
}) => {
  const selectableItemRefs = bookingItems
    .filter(({ cancelled, isActive, isPlaceholder }) => !cancelled && isActive !== false && !isPlaceholder)
    .map(({ itemRef }) => itemRef);
  const columns = [
    ...ColumnKeys,
    isSelectable && {
      key: 'select',
      format: ({ cancelled, isActive, isPlaceholder, itemRef }) => {
        const itemRefs = [itemRef];

        return (
          <input
            type="checkbox"
            checked={isGroupChecked(itemRefs)}
            onChange={toggleGroup(itemRefs)}
            disabled={cancelled || isActive === false || isPlaceholder}
          />
        );
      }
    }
  ].filter(Boolean);
  const formatAddOnHeader = (column) => {
    if (column.key === 'select') {
      return (
        <input
          type="checkbox"
          checked={isGroupChecked(selectableItemRefs)}
          onChange={toggleGroup(selectableItemRefs)}
          disabled={!selectableItemRefs.length}
        />
      );
    }

    return formatHeader(column);
  };

  return (
    <Leg
      isAFareAddOn
      direction={direction}
      origin={leg.origin}
      destination={leg.destination}
      numberOfPassengers={activeBookingItems.length}
      includeFareConditions={includeFareConditions}
      classOfService={leg.classOfService}
      afterSaleConditions={<AfterSalesSummaryBox direction={leg} />}
      date={leg.timing.isoDate}
      departureTime={<PsuedoStationDepartureTime isBeforeJourney={leg?.origin?.isPseudoStation} />}
      journey={leg}
    >
      <Table
        borderColor={colours.Greyscale.Black}
        headerColor={colours.Greyscale.Black}
        headerBackground={colours.Greyscale.White}
        formatHeader={formatAddOnHeader}
        rows={bookingItems.map((bookingItem) => mapToPassengerData(bookingItem, isSelectable))}
        columns={columns}
      />
    </Leg>
  );
};

FareAddOnLeg.propTypes = {
  direction: PropTypes.string,
  leg: legShape,
  includeFareConditions: PropTypes.bool,
  activeBookingItems: productsShape,
  formatHeader: PropTypes.func,
  bookingItems: productsShape,
  isGroupChecked: PropTypes.func,
  isSelectable: PropTypes.bool,
  toggleGroup: PropTypes.func
};

export default FareAddOnLeg;
